import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  DietGroupedResponse,
  DietPreference,
  MealOption,
} from "../Domain/MealSettings";

import {
  fetchDietPreferences,
  fetchMealOptions,
} from "../Domain/dietSettingsApi";
import { MealOptionUtils } from "../Domain/mealOptionsUtil";

type MealOptionsState = {
  isModified: boolean;
  mealOptions: MealOption[];
  dietPreferencesResponse: DietGroupedResponse[];
};

type MealOptionsActions = {
  initializeState: (clientId: string, categoryId: string) => Promise<void>;
  toggleShow: (mealOption: MealOption) => void;
  toggleToBeOrdered: (mealOption: MealOption) => void;
  toggleFoodFree: (mealOption: MealOption) => void;
  modifyDietPreferences: (
    mealOption: MealOption,
    dietPreferences: DietPreference[]
  ) => void;
  modifyDietPreferencesSetAll: (dietPreferences: DietPreference[]) => void;
  setAllMealOptionShow: (checked: boolean) => void;
  setAllMealOptionTobeOrdered: (checked: boolean) => void;
  setAllMealOptionFoodFree: (checked: boolean) => void;
  removeModifiedState: () => void;
};

export const useMealOptionsStore = create<
  MealOptionsState & MealOptionsActions
>()(
  persist(
    (set) => ({
      isModified: false,
      mealOptions: [],
      dietPreferencesResponse: [],
      initializeState: async (clientId: string, categoryId: string) => {
        const clientDietResponse = await fetchMealOptions(clientId, categoryId);
        const dietGroupedResponses = await fetchDietPreferences(categoryId);

        const cachedClientId = sessionStorage.getItem("clientId");
        if (cachedClientId !== clientId) {
          sessionStorage.setItem("clientId", clientId);
        }

        set({
          isModified: false,
          mealOptions: clientDietResponse.mealOptions,
          dietPreferencesResponse: dietGroupedResponses,
        });
      },
      removeModifiedState: () => {
        set({ isModified: false });
      },

      toggleShow: (mealOption: MealOption) => {
        set((state) => ({
          isModified: true,
          mealOptions: MealOptionUtils.update(
            state.mealOptions,
            MealOptionUtils.updateDisabled(mealOption, !mealOption.disabled)
          ),
        }));
      },

      toggleToBeOrdered: (mealOption: MealOption) => {
        set((state) => ({
          isModified: true,
          mealOptions: MealOptionUtils.update(
            state.mealOptions,
            MealOptionUtils.updateToBeOrdered(
              mealOption,
              !mealOption.toBeOrdered
            )
          ),
        }));
      },

      toggleFoodFree: (mealOption: MealOption) => {
        set((state) => ({
          isModified: true,
          mealOptions: MealOptionUtils.update(
            state.mealOptions,
            MealOptionUtils.updateFoodFree(mealOption, !mealOption.foodFree)
          ),
        }));
      },

      modifyDietPreferences: (
        mealOption: MealOption,
        dietPreferences: DietPreference[]
      ) => {
        set((state) => ({
          isModified: true,
          mealOptions: MealOptionUtils.update(
            state.mealOptions,
            MealOptionUtils.updateDietPreferences(mealOption, dietPreferences)
          ),
        }));
      },

      modifyDietPreferencesSetAll: (dietPreferences: DietPreference[]) => {
        set((state) => ({
          isModified: true,
          mealOptions: MealOptionUtils.updateAll(
            state.mealOptions,
            dietPreferences
          ),
        }));
      },

      setAllMealOptionShow: (show: boolean) => {
        set((state) => ({
          isModified: true,
          mealOptions: state.mealOptions.map((option) =>
            MealOptionUtils.updateDisabled(option, !show)
          ),
        }));
      },

      setAllMealOptionTobeOrdered: (checked: boolean) => {
        set((state) => ({
          isModified: true,
          mealOptions: state.mealOptions.map((option) =>
            MealOptionUtils.updateToBeOrdered(option, checked)
          ),
        }));
      },

      setAllMealOptionFoodFree: (checked: boolean) => {
        set((state) => ({
          isModified: true,
          mealOptions: state.mealOptions.map((option) =>
            MealOptionUtils.updateFoodFree(option, checked)
          ),
        }));
      },
    }),
    {
      name: "mealOptions",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
